import React, { useState } from 'react';
import { View, StyleSheet, ActivityIndicator, Platform, Image, Pressable } from 'react-native';
import * as AppleAuthentication from 'expo-apple-authentication';
import { 
  PageContainer, 
  ContentContainer, 
  Text, 
  Button, 
  Input, 
  Theme 
} from '../../components/ui';

interface AuthTemplateProps {
  mode: 'login' | 'register';
  title: string;
  subtitle?: string;
  
  // Email/password form
  email: string;
  password: string;
  emailError: string;
  passwordError: string;
  onEmailChange: (text: string) => void;
  onPasswordChange: (text: string) => void;
  onEmailBlur: () => void;
  onPasswordBlur: () => void;
  onEmailPasswordSubmit: () => void;
  emailPasswordLoading: boolean;
  
  // Social auth
  onApplePress?: () => void;
  appleLoading?: boolean;
  onGooglePress?: () => void;
  googleLoading?: boolean;
  
  // Additional actions
  onSecondaryAction?: () => void;
  secondaryActionLabel?: string;
  onForgotPassword?: () => void;
  onAnonymousLogin?: () => void;
}

const AuthTemplate: React.FC<AuthTemplateProps> = ({
  mode,
  title,
  subtitle,
  email,
  password,
  emailError,
  passwordError,
  onEmailChange,
  onPasswordChange,
  onEmailBlur,
  onPasswordBlur,
  onEmailPasswordSubmit,
  emailPasswordLoading,
  onApplePress,
  appleLoading = false,
  onGooglePress,
  googleLoading = false,
  onSecondaryAction,
  secondaryActionLabel,
  onForgotPassword,
  onAnonymousLogin,
}) => {
  const [showEmailForm, setShowEmailForm] = useState(Platform.OS === 'web');

  // Platform-specific main button order
  const isIOS = Platform.OS === 'ios';
  const isAndroid = Platform.OS === 'android';
  const isWeb = Platform.OS === 'web';

  return (
    <PageContainer>
      <ContentContainer>
        <View style={styles.content}>
          <Text variant="heading1" align="center" style={styles.title}>
            {title}
          </Text>
          
          {subtitle && (
            <Text variant="body1" color="secondary" align="center" style={styles.subtitle}>
              {subtitle}
            </Text>
          )}
          
          <View style={styles.buttonsContainer}>
            {/* Main platform button - Apple on iOS, Google on Android/Web */}
            {isIOS && onApplePress && (
              <View style={styles.appleButtonWrapper}>
                <AppleAuthentication.AppleAuthenticationButton
                  buttonType={
                    mode === 'register'
                      ? AppleAuthentication.AppleAuthenticationButtonType.SIGN_UP
                      : AppleAuthentication.AppleAuthenticationButtonType.SIGN_IN
                  }
                  buttonStyle={AppleAuthentication.AppleAuthenticationButtonStyle.BLACK}
                  cornerRadius={8}
                  style={styles.appleButton}
                  onPress={onApplePress}
                />
                {appleLoading && (
                  <ActivityIndicator
                    style={styles.buttonLoader}
                    color={Theme.colors.primary}
                  />
                )}
              </View>
            )}

            {/* Icon buttons row - native only */}
            {!isWeb && (
              <View style={styles.iconButtonsRow}>
                {/* Google icon (iOS and Android) */}
                {onGooglePress && (
                  <Pressable 
                    style={styles.iconButton}
                    onPress={onGooglePress}
                    disabled={googleLoading}
                  >
                    <Image 
                      source={require('../../assets/google-icon.png')} 
                      style={styles.iconImage}
                      resizeMode="contain"
                    />
                    {googleLoading && (
                      <ActivityIndicator
                        style={styles.iconLoader}
                        size="small"
                        color={Theme.colors.primary}
                      />
                    )}
                  </Pressable>
                )}

                {/* Email */}
                <Pressable 
                  style={[styles.iconButton, showEmailForm && styles.iconButtonActive]}
                  onPress={() => setShowEmailForm(!showEmailForm)}
                >
                  <Image 
                    source={require('../../assets/email-icon.png')} 
                    style={styles.iconImage}
                    resizeMode="contain"
                  />
                </Pressable>
              </View>
            )}

            {/* Email/Password Form */}
            {showEmailForm && (
              <View style={styles.emailFormContainer}>
                <Input
                  placeholder="Email"
                  value={email}
                  onChangeText={onEmailChange}
                  onBlur={onEmailBlur}
                  keyboardType="email-address"
                  autoCapitalize="none"
                  editable={!emailPasswordLoading}
                  error={!!emailError}
                  errorMessage={emailError}
                />
                
                <Input
                  placeholder={mode === 'register' ? 'Password (min. 6 characters)' : 'Password'}
                  value={password}
                  onChangeText={onPasswordChange}
                  onBlur={onPasswordBlur}
                  secureTextEntry
                  autoCapitalize="none"
                  keyboardType="default"
                  editable={!emailPasswordLoading}
                  error={!!passwordError}
                  errorMessage={passwordError}
                />
                
                {emailPasswordLoading ? (
                  <ActivityIndicator 
                    size="large" 
                    color={Theme.colors.primary} 
                    style={styles.loader}
                  />
                ) : (
                  <>
                    <Button 
                      title={mode === 'register' ? 'Create Account' : 'Sign In'}
                      onPress={onEmailPasswordSubmit}
                      style={styles.submitButton}
                      disabled={emailPasswordLoading}
                    />
                    
                    {mode === 'login' && onForgotPassword && (
                      <Button
                        title="Forgot Password?"
                        variant="link"
                        onPress={onForgotPassword}
                        disabled={emailPasswordLoading}
                        style={styles.linkButton}
                      />
                    )}
                  </>
                )}
                
                {/* Web: Social auth buttons with branded styling */}
                {isWeb && (
                  <View style={styles.webSocialButtons}>
                    {onGooglePress && (
                      <Pressable
                        onPress={onGooglePress}
                        disabled={googleLoading}
                        style={styles.googleBrandedButton}
                      >
                        <Image 
                          source={require('../../assets/google-icon.png')} 
                          style={styles.brandedButtonIcon}
                          resizeMode="contain"
                        />
                        <Text style={styles.googleButtonText}>Continue with Google</Text>
                        {googleLoading && (
                          <ActivityIndicator
                            style={styles.brandedButtonLoader}
                            color="#4285F4"
                            size="small"
                          />
                        )}
                      </Pressable>
                    )}
                    {onApplePress && (
                      <Pressable
                        onPress={onApplePress}
                        disabled={appleLoading}
                        style={styles.appleBrandedButton}
                      >
                        <Image 
                          source={require('../../assets/apple-icon.png')} 
                          style={styles.brandedButtonIcon}
                          resizeMode="contain"
                        />
                        <Text style={styles.appleButtonText}>Continue with Apple</Text>
                        {appleLoading && (
                          <ActivityIndicator
                            style={styles.brandedButtonLoader}
                            color="#FFFFFF"
                            size="small"
                          />
                        )}
                      </Pressable>
                    )}
                  </View>
                )}
              </View>
            )}

            {/* "I already have an account" / "Create Account" link */}
            {onSecondaryAction && secondaryActionLabel && (
              <View style={styles.secondaryActionContainer}>
                <Text variant="body1" color="secondary" align="center">
                  {mode === 'register' ? 'I already have an account' : "Don't have an account?"}
                </Text>
                <Button
                  title={secondaryActionLabel}
                  variant="link"
                  onPress={onSecondaryAction}
                  disabled={emailPasswordLoading}
                  style={styles.linkButton}
                />
              </View>
            )}

            {/* Anonymous login for register screen */}
            {mode === 'register' && onAnonymousLogin && (
              <Button
                title="I'll do it later"
                variant="secondary"
                onPress={onAnonymousLogin}
                style={styles.anonymousButton}
                disabled={emailPasswordLoading}
              />
            )}
          </View>
        </View>
      </ContentContainer>
    </PageContainer>
  );
};

const styles = StyleSheet.create({
  content: {
    flex: 1,
    justifyContent: 'center',
    paddingHorizontal: Theme.spacing.lg,
  },
  title: {
    marginBottom: Theme.spacing.md,
    fontFamily: Theme.typography.fontFamily.semiBold,
  },
  subtitle: {
    marginBottom: Theme.spacing.xl,
    paddingHorizontal: Theme.spacing.md,
  },
  buttonsContainer: {
    gap: Theme.spacing.lg,
    marginTop: Theme.spacing.xl,
  },
  appleButtonWrapper: {
    position: 'relative',
  },
  appleButton: {
    width: '100%',
    height: 44,
  },
  mainButton: {
    // Uses default button styling
  },
  googleButtonWrapper: {
    position: 'relative',
  },
  buttonLoader: {
    position: 'absolute',
    right: Theme.spacing.md,
    top: '50%',
    marginTop: -10,
  },
  iconButtonsRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: Theme.spacing.md,
  },
  iconButton: {
    width: 64,
    height: 64,
    borderRadius: 12,
    backgroundColor: Theme.colors.surface,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: Theme.colors.border,
    position: 'relative',
  },
  iconButtonActive: {
    borderColor: Theme.colors.primary,
    backgroundColor: Theme.colors.primaryLight,
  },
  iconImage: {
    width: 32,
    height: 32,
  },
  iconLoader: {
    position: 'absolute',
  },
  emailFormContainer: {
    gap: Theme.spacing.md,
    marginTop: Theme.spacing.md,
  },
  submitButton: {
    marginTop: Theme.spacing.sm,
  },
  linkButton: {
    alignSelf: 'center',
  },
  secondaryActionContainer: {
    gap: Theme.spacing.sm,
    alignItems: 'center',
    marginTop: Theme.spacing.lg,
  },
  anonymousButton: {
    marginTop: Theme.spacing.md,
  },
  loader: {
    marginVertical: Theme.spacing.xl,
  },
  webSocialButtons: {
    gap: Theme.spacing.sm,
    marginTop: Theme.spacing.md,
  },
  googleBrandedButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E0E0E0',
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: Theme.spacing.lg,
    position: 'relative',
  },
  appleBrandedButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#000000',
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: Theme.spacing.lg,
    position: 'relative',
  },
  brandedButtonIcon: {
    width: 20,
    height: 20,
    marginRight: Theme.spacing.sm,
  },
  googleButtonText: {
    fontSize: 16,
    fontFamily: Theme.typography.fontFamily.medium,
    color: '#3C4043',
  },
  appleButtonText: {
    fontSize: 16,
    fontFamily: Theme.typography.fontFamily.medium,
    color: '#FFFFFF',
  },
  brandedButtonLoader: {
    position: 'absolute',
    right: Theme.spacing.md,
  },
});

export default AuthTemplate;

